import { differenceInMinutes, max, min, parseISO } from "date-fns";

import { type ShiftTimeData } from "./types";

export const MAX_ALLOWED_OVERLAP_IN_MINUTES = 30;

/**
 * Checks if two shifts overlap by more than 30 minutes.
 *
 * Overlap is calculated as: min(end1, end2) - max(start1, start2).
 * If the result is greater than MAX_ALLOWED_OVERLAP_IN_MINUTES, the shifts
 * are considered to excessively overlap. Only use this for shifts at the same workplace.
 */
export function doShiftsExcessivelyOverlap(shiftA: ShiftTimeData, shiftB: ShiftTimeData): boolean {
  const startA = parseISO(shiftA.start);
  const endA = parseISO(shiftA.end);
  const startB = parseISO(shiftB.start);
  const endB = parseISO(shiftB.end);

  const overlapStart = max([startA, startB]);
  const overlapEnd = min([endA, endB]);

  const overlapMinutes = differenceInMinutes(overlapEnd, overlapStart);

  return overlapMinutes > MAX_ALLOWED_OVERLAP_IN_MINUTES;
}

/**
 * Checks if a proposed shift excessively overlaps with any shift in the list.
 */
export function wouldProposedShiftCauseOverlap(
  proposedShift: ShiftTimeData,
  existingShifts: ShiftTimeData[]
): boolean {
  return existingShifts.some((existing) => doShiftsExcessivelyOverlap(proposedShift, existing));
}
